import {
  buildPercentChange,
  buildPointChange,
  buildVolumeChange,
  formatMetricValue,
} from './results-format';
import { EMPTY_VALUE, OVERVIEW_METRICS } from '../results-step.constants';

import type {
  ActualResults,
  AidModelResultRowDto,
  EstimatedResults,
} from 'api/generated/schemas';
import type {
  ChangeValue,
  MetricDefinition,
  OverviewRow,
} from '../results-step.types';

const readField = (
  row: AidModelResultRowDto | undefined,
  field: keyof AidModelResultRowDto | null
): number | null | undefined => {
  if (!row || !field) return null;

  const value = row[field];

  return typeof value === 'number' ? value : null;
};

const buildRow = (
  metric: MetricDefinition,
  actualYearRow: AidModelResultRowDto | undefined,
  modeledActualRow: AidModelResultRowDto | undefined,
  modeledThisYearRow: AidModelResultRowDto | undefined
): OverviewRow => {
  if (!metric.field) {
    return {
      key: metric.key,
      metric: metric.label,
      kind: metric.kind,
      actualYear: EMPTY_VALUE,
      modeledActualYear: EMPTY_VALUE,
      modeledThisYear: EMPTY_VALUE,
      percentChange: null,
      volumeChange: null,
    };
  }

  const actualYearValue = readField(actualYearRow, metric.field);
  const modeledActualValue = readField(modeledActualRow, metric.field);
  const modeledThisYearValue = readField(modeledThisYearRow, metric.field);

  const FLIP_DIRECTION: Record<
    ChangeValue['direction'],
    ChangeValue['direction']
  > = {
    up: 'down',
    down: 'up',
    neutral: 'neutral',
  };

  const applyBadgeModifier = (
    change: ChangeValue | null
  ): ChangeValue | null => {
    if (!change) return null;
    if (metric.neutralBadge) return { ...change, direction: 'neutral' };
    if (metric.invertBadge)
      return { ...change, direction: FLIP_DIRECTION[change.direction] };

    return change;
  };

  const rawPercentChange =
    metric.kind === 'rate'
      ? buildPointChange(modeledThisYearValue, actualYearValue)
      : buildPercentChange(modeledThisYearValue, actualYearValue);

  const rawVolumeChange =
    metric.kind === 'rate'
      ? null
      : buildVolumeChange(modeledThisYearValue, actualYearValue, metric.kind);

  const percentChange = applyBadgeModifier(rawPercentChange);
  const volumeChange = applyBadgeModifier(rawVolumeChange);

  return {
    key: metric.key,
    metric: metric.label,
    kind: metric.kind,
    actualYear: formatMetricValue(actualYearValue, metric.kind),
    modeledActualYear: formatMetricValue(modeledActualValue, metric.kind),
    modeledThisYear: formatMetricValue(modeledThisYearValue, metric.kind),
    percentChange,
    volumeChange,
  };
};

/**
 * Builds the Tab 1 comparison rows. Change columns compare MODELED THIS YEAR
 * (estimated projection) against ACTUAL YEAR (actual results) per ADO #35373.
 */
export const buildOverviewRows = (
  actualResults: ActualResults | undefined,
  estimatedResults: EstimatedResults | undefined
): OverviewRow[] =>
  OVERVIEW_METRICS.map((metric) =>
    buildRow(
      metric,
      actualResults?.actual,
      actualResults?.model,
      estimatedResults?.newProjectedModel
    )
  );
